import styled from '@emotion/styled'
import React, { useCallback, useState } from 'react'
import { useMutation } from '../../../api/graphql'
import { useChatContext } from './ChatProvider'
import { CONVERSATION_CREATE_MUTATION } from './chatQueries'

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 12px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`

const SubjectInput = styled.input`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-family: inherit;
  outline: none;

  &:focus {
    border-color: #102638;
    box-shadow: 0 0 0 2px rgba(16, 38, 56, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`

const MessageArea = styled.textarea`
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  outline: none;
  min-height: 120px;

  &:focus {
    border-color: #102638;
    box-shadow: 0 0 0 2px rgba(16, 38, 56, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`

const SubmitButton = styled.button`
  background: #102638;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #1a3a52;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`

const NewConversation: React.FC = () => {
  const { openConversation, refetchUnread } = useChatContext()
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const [createConversation] = useMutation(CONVERSATION_CREATE_MUTATION)

  const handleSubmit = useCallback(async () => {
    const trimmedBody = body.trim()
    if (!trimmedBody || submitting) return

    setSubmitting(true)
    try {
      const { data } = await createConversation({
        variables: {
          input: {
            body: trimmedBody,
            subject: subject.trim() || undefined,
            pageContext: JSON.stringify({
              path: window.location.pathname,
              url: window.location.href,
            }),
          },
        },
      })

      const conversation = data?.conversationCreate?.conversation
      if (conversation) {
        refetchUnread()
        openConversation(conversation.id)
      }
    } finally {
      setSubmitting(false)
    }
  }, [body, subject, submitting, createConversation, openConversation, refetchUnread])

  return (
    <Container>
      <FieldGroup>
        <Label htmlFor="chat-subject">Subject (optional)</Label>
        <SubjectInput
          id="chat-subject"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          placeholder="Brief description of your question"
        />
      </FieldGroup>
      <FieldGroup style={{ flex: 1 }}>
        <Label htmlFor="chat-body">Message</Label>
        <MessageArea
          id="chat-body"
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="How can we help you?"
          autoFocus
        />
      </FieldGroup>
      <SubmitButton onClick={handleSubmit} disabled={submitting || !body.trim()}>
        {submitting ? 'Sending...' : 'Send Message'}
      </SubmitButton>
    </Container>
  )
}

export default NewConversation
